import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import propTypes from 'prop-types';
import axios from 'axios';
import { useAuth } from '../../../../Contexts/AuthContext';
import { StyledButton } from '../../../../Components/Generics.styled';
import CircularProgress from '@mui/material/CircularProgress';

const WhereYouWantAdAppearContainer = styled.div`
    margin-top: 100px;
    display: flex;
    align-items: center;
    width: 100%;

    flex-direction: column;

    .MuiOutlinedInput-root {
        background-color: #fafbf7;
    }
`;

const Title = styled.div`
    font-size: 23px;
    font-weight: bold;
    letter-spacing: 0px;
    color: #000000;
    opacity: 1;
    height: 50px;
    flex-direction: column;
    font-weight: bold;
    text-align: center;
`;

const SubText = styled.div`
    margin-top: 20px;
    text-align: center;
    letter-spacing: 0px;
    max-width: 500px;
    color: #707070;
    opacity: 1;
    margin-left: auto;
    margin-right: auto;
`;

const SelectorContainer = styled.div`
    margin-top: 30px;
    margin-left: auto;
    margin-right: auto;
    justify-content: center;
    width: 100%;
    max-width: 800px;
`;

const StyledNavButtons = styled.div`
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 160px;
    margin-left: auto;
    margin-right: auto;
`;

const WhereYouWantAdAppear = (props) => {
    const { campaignData, setCampaignData, handleNavButtonBack, handleNavButtonNext } = props;
    const [locationVals, setLocationVals] = useState([]);
    const [postData, setPostData] = useState({
        isLoading: false,
        error: false,
        success: false,
        message: '',
    });

    const [searchQuery, setSearchQuery] = useState('');
    const { currentUser } = useAuth();
    const { accessToken } = { ...currentUser };
    const baseURL = process.env.REACT_APP_API_BASE_URL;

    const isGoogle = campaignData?.campaignTypeId === 1;
    const isProgrammatic = campaignData?.campaignTypeId === 26;
    const isMobile = campaignData?.campaignTypeId === 2;

    const handleLocationVals = (e) => {
        setSearchQuery(e.target.value);
    };

    const options = {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json;charset=UTF-8',
            Authorization: `Bearer ${accessToken}`,
        },
    };

    const getData = async () => {
        if (isProgrammatic) {
            try {
                setPostData({ ...postData, isLoading: true });
                const locationData = await axios.get(
                    `${baseURL}/GetZipCodeLocations/${searchQuery}`,
                    options
                );

                if (locationData.data.length > 0) {
                    setPostData({ ...postData, isLoading: false, success: true });
                    setLocationVals(locationData.data);
                } else {
                    setPostData({
                        ...postData,
                        isLoading: false,
                        success: false,
                        error: false,
                        message: 'Something went wrong.',
                    });
                }
            } catch (err) {
                setPostData({
                    ...postData,
                    isLoading: false,
                    success: false,
                    error: false,
                    message: 'Something went wrong.',
                });
            }
        } else if (isGoogle || isMobile) {
            try {
                setPostData({ ...postData, isLoading: true });

                const locationData = await axios.get(
                    `${baseURL}/GetGoogleGeoTargetLocations/${searchQuery}`,
                    options
                );
                if (locationData?.data.length > 0) {
                    setPostData({ ...postData, isLoading: false, success: true });
                    setLocationVals(locationData?.data);
                } else {
                    setPostData({
                        ...postData,
                        isLoading: false,
                        success: false,
                        error: false,
                        message: 'Something went wrong.',
                    });
                }
            } catch (err) {
                setPostData({
                    ...postData,
                    isLoading: false,
                    success: false,
                    error: false,
                    message: 'Something went wrong.',
                });
            }
        }
    };

    useEffect(() => {
        if ((isGoogle || isMobile) && searchQuery?.length > 3) {
            getData();
        }

        if (isProgrammatic && searchQuery?.length > 5) {
            getData();
        }
    }, [searchQuery]);

    const formatOptionLabel = (option) => {
        const { cityName, countyName, stateName } = option;

        if (isGoogle || isMobile) {
            return `${option?.canonicalName?.replace(/,/g, ', ')} (Reach: ${option?.reach})`;
        }

        if (cityName && countyName && stateName) {
            return `${cityName}, ${countyName}, ${stateName}`;
        }

        if (!cityName && countyName && stateName) {
            return `${countyName}, ${stateName}`;
        }

        if (cityName && !countyName && stateName) {
            return `${cityName}, ${stateName}`;
        }

        if (cityName && countyName && !stateName) {
            return `${cityName}, ${countyName}`;
        }

        if (!cityName && !countyName && stateName) {
            return `${stateName} (State)`;
        }

        if (cityName && !countyName && !stateName) {
            return `${cityName} (City)`;
        }

        if (!cityName && countyName && !stateName) {
            return `${countyName} (County)`;
        }
    };

    return (
        <WhereYouWantAdAppearContainer>
            <Title>Where do you want your Ad to appear?</Title>
            <SubText>
                In the field below, type the locations you would like your ads to be displayed in.
                You can type Countries, States, Cities or Zip Codes.
            </SubText>
            <SelectorContainer>
                <Autocomplete
                    multiple
                    id="Location"
                    loading={postData?.isLoading}
                    options={locationVals}
                    value={campaignData?.locations || ['']}
                    getOptionLabel={(option) => formatOptionLabel(option)}
                    onChange={(e, value) =>
                        setCampaignData({
                            ...campaignData,
                            locations: value,
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            onChange={(e) => handleLocationVals(e)}
                            variant="outlined"
                            {...params}
                            label="Location"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {postData?.isLoading && (
                                            <CircularProgress color="inherit" size={20} />
                                        )}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            placeholder="locations"
                            error={campaignData?.locations.length < 1}
                            helperText={
                                campaignData?.locations.length < 1
                                    ? 'You must select at least one location.'
                                    : ''
                            }
                        />
                    )}
                />
            </SelectorContainer>
            <br />
            <br />
            <StyledNavButtons>
                <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={() => handleNavButtonBack()}
                >
                    Back
                </StyledButton>
                <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={() => handleNavButtonNext()}
                    disabled={campaignData?.locations.length < 1}
                >
                    Next
                </StyledButton>
            </StyledNavButtons>
        </WhereYouWantAdAppearContainer>
    );
};

WhereYouWantAdAppear.propTypes = {
    campaignData: propTypes.shape({
        locations: propTypes.oneOfType([propTypes.string, propTypes.array]),
        campaignTypeName: propTypes.string,
        campaignTypeId: propTypes.number,
    }),
    setCampaignData: propTypes.func,
    handleNavButtonNext: propTypes.func,
    handleNavButtonBack: propTypes.func,
};

WhereYouWantAdAppear.defaultProps = {
    campaignData: { acceptedPlaceIds: {} },
    setCampaignData: () => {},
    handleNavButtonNext: () => {},
    handleNavButtonBack: () => {},
};

export default WhereYouWantAdAppear;
